import { SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import Layout from "../../app/layout/Layout";
import { Site } from "../../app/models/site";
import agent from "../../app/api/agent";
import LoadingComponent from "../../app/layout/LoadingComponent";
import dayjs, { Dayjs } from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { useOrganization } from "@clerk/clerk-react";
import {
  EuiEmptyPrompt,
  EuiLink,
  EuiSelect,
  EuiSpacer,
  EuiSuperDatePicker,
  EuiSuperDatePickerProps,
  EuiTitle,
  OnRefreshProps,
  OnTimeChangeProps,
} from "@elastic/eui";
import { EuiPageHeader, EuiButton } from "@elastic/eui";
import { Add } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useFlow } from "@frigade/react";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { LoadingButton } from "@mui/lab";
import { disableSite, enableSite } from "./catalogSlice";
import { toast } from "react-toastify";
import { useAppDispatch } from "../../app/store/configureStore";

export default function Control() {
  const [selectedOption, setSelectedOption] = useState("");
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState<Site[]>([]);
  const { organization } = useOrganization();

  const handleOptionChange = (event: any) => {
    setSelectedOption(event.target.value);
  };

  const [recentlyUsedRanges, setRecentlyUsedRanges] = useState<
    NonNullable<EuiSuperDatePickerProps["recentlyUsedRanges"]>
  >([]);

  const [isLoading, setIsLoading] = useState(false);

  const [start, setStart] = useState("now-12h");
  const [end, setEnd] = useState("now");

  const [refreshKey, setRefreshKey] = useState(Date.now());
  const dispatch = useAppDispatch();
    const [submitting, setSubmitting] = useState(false);

   async function submitDisable(id: string) {
      try {
        setSubmitting(true);
        await dispatch(disableSite(id));
        setSubmitting(false);
        toast.success("Site stopped successfully");
      } catch (error) {
        console.log("[Login] login user");
      }
    }
  
    async function submitEnable(id: string) {
      try {
        setSubmitting(true);
        await dispatch(enableSite(id));
        setSubmitting(false);
        toast.success("Site started successfully");
      } catch (error) {
        console.log("[Login] login user");
      }
    }

  const onTimeChange = ({ start, end }: OnTimeChangeProps) => {
    const recentlyUsedRange = recentlyUsedRanges.filter((recentlyUsedRange) => {
      const isDuplicate =
        recentlyUsedRange.start === start && recentlyUsedRange.end === end;
      return !isDuplicate;
    });
    recentlyUsedRange.unshift({ start, end });
    setStart(start);
    setEnd(end);
    setRecentlyUsedRanges(
      recentlyUsedRange.length > 10
        ? recentlyUsedRange.slice(0, 9)
        : recentlyUsedRange
    );
    setIsLoading(true);
    startLoading();
  };

  const onRefresh = ({ start, end }: OnRefreshProps) => {
    setRefreshKey(Date.now()); // force a re-render
    setIsLoading(true);
    startLoading();
  };

  const startLoading = () => {
    setTimeout(stopLoading, 1000);
  };

  const stopLoading = () => {
    setIsLoading(false);
  };

  const { flow } = useFlow("flow_6fi97SYs");

  useEffect(() => {
    setLoading(true);
    agent.Catalog.list()
      .then((sites) => {
        setSites(sites);
        if (sites.length > 0) {
          flow?.steps.get("step-5")?.complete();
        }
      })
      .catch((error) => console.log(error))
      .finally(() => setLoading(false));
  }, [organization?.id, flow]);

  const siteArray = sites.map((site) => site.hackgatedName);

  const selectedSite = sites.find(
    (site) => site.hackgatedName === selectedOption
  );

  if (loading) return <LoadingComponent message="Loading website data..." />;

  if (sites.length === 0)
    return (
      <Layout>
        <EuiPageHeader
          pageTitle="Control Module"
          // iconType="logoKibana"
          description="Enforces scope boundaries by blocking unauthorized paths and logging tester activity, enhancing accountability and reducing risk."
          // rightSideItems={[
          //   <EuiButton fill>Add something</EuiButton>,
          //   <EuiButton>Do something</EuiButton>,
          // ]}
          paddingSize="none"
        />
        <EuiSpacer />

        <Card variant="outlined" sx={{ mb: 2, borderRadius: "12px" }}>
          <CardContent>
            <Box
              sx={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EuiEmptyPrompt
                actions={[
                  <EuiButton color="primary" fill>
                    Add a website
                  </EuiButton>,
                ]}
                body={<p>Add a new case or change your filter settings.</p>}
                color="subdued"
                // iconType="logoSecurity"
                layout="vertical"
                title={<h2>Start adding websites</h2>}
                titleSize="m"
              />
            </Box>
          </CardContent>
        </Card>
      </Layout>
    );

  return (
    <Layout>
      <EuiPageHeader
        pageTitle="Control Module"
        // iconType="logoKibana"
        description="Enforces scope boundaries by blocking unauthorized paths and logging tester activity, enhancing accountability and reducing risk."
        // rightSideItems={[
        //   <EuiButton fill>Add something</EuiButton>,
        //   <EuiButton>Do something</EuiButton>,
        // ]}
        paddingSize="none"
      />
      <EuiSpacer />

      {/* <EuiSelect
        options={siteArray.map((hackgatedName, index) => ({ value: hackgatedName, text: hackgatedName }))}
        onChange={(e) => {
          // Handle the select change event
          console.log('Selected value:', e.target.value);
        }}
      /> */}

      <Grid container alignItems="center" spacing={3} mb={3}>
        <Grid item xs={4}>
          <FormControl fullWidth size="small">
            <InputLabel id="site-select-label">Select a site</InputLabel>
            <Select
              labelId="site-select-label"
              id="site-select"
              value={selectedOption}
              label="Select a site"
              onChange={handleOptionChange}
              sx={{ borderRadius: "8px" }}
            >
              {sites.map((sites) => (
                <MenuItem key={sites.hackgatedName} value={sites.hackgatedName}>
                  {sites.hackgatedName}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={8}></Grid>
      </Grid>

      <Card variant="outlined" sx={{ mb: 2, borderRadius: "12px" }}>
        <CardContent>
          {selectedOption === "" ? (
            <Box
              sx={{
                height: 400,
                width: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <EuiEmptyPrompt
                // iconType="logoSecurity"
                title={<h2>Select a website</h2>}
                body={
                  <p>
                    Select or add a new website or change your filter settings.
                  </p>
                }
                actions={
                  <Button
                    component={Link}
                    to="/catalog/add-site"
                    variant="contained"
                    startIcon={<Add />}
                  >
                    Add a website
                  </Button>
                }
                footer={
                  <>
                    <EuiTitle size="xxs">
                      <h3>Want to learn more?</h3>
                    </EuiTitle>
                    <EuiLink
                      href="https://www.hackgate.io/how-does-hackgate-work"
                      target="_blank"
                    >
                      Read the docs
                    </EuiLink>
                  </>
                }
              />
            </Box>
          ) : (
            ""
          )}

          {selectedOption !== "" ? (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 1, borderRadius: "12px" }}>
                  <CardContent>
                    <Typography variant="h6" mb={2}>
                      Allowed Ethical Hackers
                    </Typography>
                    <Typography>
                      {selectedSite?.hasCustomList === false
                        ? "Everyone"
                        : "Custom List"}
                    </Typography>
                    <Button
                      component={Link}
                      to = {`/catalog/${selectedSite?.id}/hackers`}
                      variant="outlined"
                      sx={{ borderRadius: "8px" }}
                    >
                      Edit
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 1, borderRadius: "12px" }}>
                  <CardContent>
                    <Typography variant="h6" mb={2}>
                      Path Blocking
                    </Typography>
                    <Typography>
                      Disabled
                    </Typography>
                    <Button
                      component={Link}
                      to = {`/catalog/${selectedSite?.id}/hackers`}
                      variant="outlined"
                      disabled
                      sx={{ borderRadius: "8px" }}
                    >
                      Only in enterprise
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 1, borderRadius: "12px" }}>
                  <CardContent>
                    <Typography variant="h6" mb={2}>
                      Rate-limiting
                    </Typography>
                    <Typography>
                      Disabled
                    </Typography>
                    <Button
                      component={Link}
                      to = {`/catalog/${selectedSite?.id}/hackers`}
                      variant="outlined"
                      disabled
                      sx={{ borderRadius: "8px" }}
                    >
                      Only in enterprise
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
                <Grid item xs={12}>
                <Card variant="outlined" sx={{ mb: 1, borderRadius: "12px" }}>
                  <CardContent>
                    <Typography variant="h6" mb={2}>
                      Automation
                    </Typography>
                   
                          <TableRow>
                  <TableCell>Scheduled start</TableCell>
                  <TableCell>
                    <Typography>
                      {selectedSite?.startDate.toString() === "0001-01-01T00:00:00"
                        ? "Not set"
                        : selectedSite?.startDate.toString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button component={Link}   to = {`/catalog/${selectedSite?.id}/start`} variant="outlined" sx={{borderRadius: "8px"}}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>Scheduled stop</TableCell>
                  <TableCell>
                    <Typography>
                      {selectedSite?.stopDate.toString() === "0001-01-01T00:00:00"
                        ? "Not set"
                        : selectedSite?.stopDate.toString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Button component={Link} to = {`/catalog/${selectedSite?.id}/stop`} variant="outlined" sx={{borderRadius: "8px"}}>
                      Edit
                    </Button>
                  </TableCell>
                </TableRow>

                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12}>
                             <Card variant="outlined" sx={{ borderColor: "red", borderRadius: "12px" }}>
            <CardContent>
              <Typography variant="h6" mb={2}>
              <Box sx={{ fontWeight: 'bold' }}>  Advanced Actions </Box>
              </Typography>
              {selectedSite?.isActive === true && (
                <LoadingButton
                  loading={submitting}
                  onClick={() => submitDisable(selectedSite?.id)}
                >
                  Stop immediately
                </LoadingButton>
              )}
              {selectedSite?.isActive === false && (
                <LoadingButton
                  loading={submitting}
                  onClick={() => submitEnable(selectedSite?.id)}
                >
                  Start immediately
                </LoadingButton>
              )}
            </CardContent>
          </Card>
              </Grid>
              
            </Grid>
          ) : (
            ""
          )}
        </CardContent>
      </Card>
    </Layout>
  );
}
